// src/opencomponents/Header.js — V18 site header.
//
// Flat nav (no "菜单" dropdown): 公告 / 论坛 / 游戏 / 计划, plus 档案 and the
// admin entries when the session allows them. Transparent over the WebGL
// strip, solid once the page scrolls; retracts on scroll-down on the home page.

import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { color, font, HEADER_HEIGHT } from '../theme/tokens';

const Shell = styled.header`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: ${HEADER_HEIGHT}px;
    z-index: 100;
    box-sizing: border-box;
    padding: 0 40px;
    display: flex;
    align-items: center;
    gap: 48px;
    font-family: ${font.body};
    transform: translateY(${({ $visible }) => ($visible ? '0' : '-100%')});
    transition: transform 0.45s ease, background-color 0.35s ease, border-color 0.35s ease;
    border-bottom: 1px solid transparent;
    background: linear-gradient(180deg, rgba(7, 2, 5, 0.78), rgba(7, 2, 5, 0));
    ${({ $solid }) => $solid && css`
        background: rgba(7, 2, 5, 0.88);
        backdrop-filter: blur(8px);
        border-bottom-color: ${color.line};
    `}
    @media (max-width: 860px) { padding: 0 8px 0 20px; gap: 16px; justify-content: space-between; }
`;

const Logo = styled(Link)`
    display: flex;
    align-items: center;
    gap: 12px;
    color: ${color.text};
    text-decoration: none;
    flex-shrink: 0;
    span {
        font-family: ${font.display};
        font-weight: 800;
        font-size: 24px;
        letter-spacing: 0.14em;
    }
    @media (max-width: 860px) { span { font-size: 20px; } }
`;

const Nav = styled.nav`
    display: flex;
    align-items: center;
    gap: 36px;
    flex-grow: 1;
    @media (max-width: 860px) { display: none; }
`;

const Item = styled(NavLink)`
    position: relative;
    display: flex;
    align-items: center;
    height: ${HEADER_HEIGHT}px;
    font-size: 15px;
    letter-spacing: 0.12em;
    color: ${color.text2};
    text-decoration: none;
    transition: color 0.2s ease;
    white-space: nowrap;
    &::after {
        content: '';
        position: absolute;
        left: 50%;
        bottom: 12px;
        width: 5px;
        height: 5px;
        margin-left: -3px;
        transform: rotate(45deg);
        background: ${color.bloodHi};
        opacity: 0;
        transition: opacity 0.2s ease;
    }
    &:hover { color: ${color.text}; }
    &.active { color: ${color.text}; }
    &.active::after { opacity: 1; }
`;

const Right = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    flex-shrink: 0;
    @media (max-width: 860px) { display: none; }
`;

const TextBtn = styled.button`
    all: unset;
    cursor: pointer;
    font-size: 14px;
    letter-spacing: 0.12em;
    color: ${color.text2};
    &:hover { color: ${color.bloodHi}; }
    &:focus-visible { outline: 2px solid ${color.bloodHi}; outline-offset: 4px; }
`;

const GhostSmall = styled(Link)`
    display: inline-flex;
    align-items: center;
    height: 36px;
    padding: 0 18px;
    border: 1px solid ${color.line2};
    color: ${color.text2};
    font-size: 14px;
    letter-spacing: 0.12em;
    text-decoration: none;
    transition: border-color 0.2s ease, color 0.2s ease;
    &:hover { border-color: ${color.bloodHi}; color: ${color.text}; }
`;

const UserName = styled(Link)`
    font-size: 14px;
    letter-spacing: 0.08em;
    color: ${color.text};
    text-decoration: none;
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    &:hover { color: ${color.bloodHi}; }
`;

/* ---------- mobile ---------- */
const MenuButton = styled.button`
    all: unset;
    display: none;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    color: ${color.text};
    cursor: pointer;
    @media (max-width: 860px) { display: flex; }
    &:focus-visible { outline: 2px solid ${color.bloodHi}; }
`;

const Drawer = styled.div`
    position: fixed;
    top: ${HEADER_HEIGHT}px;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 99;
    background: rgba(7, 2, 5, 0.96);
    display: flex;
    flex-direction: column;
    padding: 24px 28px;
    gap: 4px;
    animation: ts-fade-in 0.2s ease;
    a, button {
        all: unset;
        cursor: pointer;
        display: flex;
        align-items: center;
        min-height: 52px;
        border-bottom: 1px solid ${color.line};
        font-family: ${font.display};
        font-size: 18px;
        letter-spacing: 0.3em;
        color: ${color.text};
    }
    a.active { color: ${color.bloodHi}; }
`;

const EyeMark = () => (
    <svg width="30" height="18" viewBox="0 0 30 18" fill="none" aria-hidden="true">
        <path d="M1 9 C 8 1, 22 1, 29 9 C 22 17, 8 17, 1 9 Z" stroke={color.blood} strokeWidth="1.4" />
        <circle cx="15" cy="9" r="4.2" fill={color.blood} />
        <circle cx="13.6" cy="7.6" r="1.1" fill={color.onBlood} />
    </svg>
);

function Header({ navItems = [], isVisible = true, solid = false }) {
    const { user, onLogout } = useAuth();
    const [drawer, setDrawer] = useState(false);
    const location = useLocation();
    const drawerRef = useRef(null);

    useEffect(() => { setDrawer(false); }, [location.pathname]);

    useEffect(() => {
        if (!drawer) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setDrawer(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [drawer]);

    const links = [
        { label: '公告', path: '/announcements' },
        { label: '论坛', path: '/forum' },
        ...navItems,
    ];

    return (
        <>
            <Shell $visible={isVisible || drawer} $solid={solid || drawer}>
                <Logo to="/" aria-label="終ノ空 首页">
                    <EyeMark />
                    <span>終ノ空</span>
                </Logo>
                <Nav aria-label="主导航">
                    {links.map((l) => <Item key={l.path} to={l.path}>{l.label}</Item>)}
                </Nav>
                <Right>
                    {user ? (
                        <>
                            <UserName to="/profile" title={user.nickname || user.username}>{user.nickname || user.username}</UserName>
                            <TextBtn type="button" onClick={onLogout}>登出</TextBtn>
                        </>
                    ) : (
                        <>
                            <Item as={NavLink} to="/login" style={{ fontSize: 14 }}>登录</Item>
                            <GhostSmall to="/register">注册</GhostSmall>
                        </>
                    )}
                </Right>
                <MenuButton type="button" aria-label={drawer ? '关闭菜单' : '打开菜单'} aria-expanded={drawer} onClick={() => setDrawer((d) => !d)}>
                    <svg width="22" height="14" viewBox="0 0 22 14" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
                        {drawer ? <path d="M4 1l14 12M18 1L4 13" /> : <path d="M1 1h20M7 7h14M1 13h20" />}
                    </svg>
                </MenuButton>
            </Shell>
            {drawer && (
                <Drawer ref={drawerRef} role="dialog" aria-label="菜单">
                    {links.map((l) => <NavLink key={l.path} to={l.path}>{l.label}</NavLink>)}
                    {user ? (
                        <button type="button" onClick={onLogout}>登出</button>
                    ) : (
                        <>
                            <NavLink to="/login">登录</NavLink>
                            <NavLink to="/register">注册</NavLink>
                        </>
                    )}
                </Drawer>
            )}
        </>
    );
}

export default Header;
